import { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { parseRoles, buildRoleString, isSubsetOf } from '../utils/roleUtils';

const STORAGE_KEY = 'devRoleEmulation';

// Only enable in development
const isDev = import.meta.env.MODE === 'development';

const initialState = { isEmulatingRoles: false, emulatedRoles: [], realRoles: [], isClearingSucursales: false };

const DevRoleContext = createContext({
  ...initialState,
  setEmulatedRoles: () => {},
  clearEmulation: () => {},
  toggleRole: () => {},
  applyPreset: () => {},
  getEffectiveRole: () => '',
  toggleSucursalEmulation: () => {},
});

export function DevRoleProvider({ children, realRole = '' }) {
  const [state, setState] = useState(initialState);

  const realRoles = useMemo(() => parseRoles(realRole), [realRole]);

  // Restore from sessionStorage on mount
  useEffect(() => {
    if (!isDev) return;

    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate stored roles are still a subset of real roles
        if (parsed.emulatedRoles && isSubsetOf(parsed.emulatedRoles, realRoles)) {
          setState({
            isEmulatingRoles: parsed.isEmulatingRoles,
            emulatedRoles: parsed.emulatedRoles,
            realRoles,
            isClearingSucursales: parsed.isClearingSucursales,
          });
          return;
        }
      }
    } catch (e) {
      console.warn('Failed to restore dev role emulation state:', e);
    }

    setState((prev) => ({ ...prev, realRoles }));
  }, [realRoles]);

  // Persist to sessionStorage
  useEffect(() => {
    if (!isDev) return;

    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          isEmulatingRoles: state.isEmulatingRoles,
          emulatedRoles: state.emulatedRoles,
          isClearingSucursales: state.isClearingSucursales,
        })
      );
    } catch (e) {
      console.warn('Failed to persist dev role emulation state:', e);
    }
  }, [state.isEmulatingRoles, state.emulatedRoles]);

  const setEmulatedRoles = useCallback(
    (roles) => {
      if (!isDev) return;

      // Validate roles are a subset of real roles
      const validRoles = roles.filter((r) => realRoles.includes(r));

      setState((prev) => ({ ...prev, isEmulatingRoles: true, emulatedRoles: validRoles }));
    },
    [realRoles]
  );

  const clearEmulation = useCallback(() => {
    if (!isDev) return;

    setState((prev) => ({ ...prev, isEmulatingRoles: false, emulatedRoles: [], isClearingSucursales: false }));

    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // Ignore
    }
  }, []);

  const toggleRole = useCallback(
    (role) => {
      if (!isDev) return;

      // Only allow toggling roles the user actually has
      if (!realRoles.includes(role)) return;

      setState((prev) => {
        // If not emulating yet, start with all real roles minus the toggled one
        if (!prev.isEmulatingRoles) {
          const newRoles = realRoles.filter((r) => r !== role);
          return { ...prev, isEmulatingRoles: true, emulatedRoles: newRoles };
        }

        // Toggle the role
        const hasRole = prev.emulatedRoles.includes(role);
        const newRoles = hasRole ? prev.emulatedRoles.filter((r) => r !== role) : [...prev.emulatedRoles, role];

        return { ...prev, emulatedRoles: newRoles };
      });
    },
    [realRoles]
  );

  const applyPreset = useCallback(
    (presetRoles) => {
      if (!isDev) return;

      // Filter to only roles the user has
      const validRoles = presetRoles.filter((r) => realRoles.includes(r));
      setState((prev) => ({ ...prev, isEmulatingRoles: true, emulatedRoles: validRoles }));
    },
    [realRoles]
  );

  const getEffectiveRole = useCallback(() => {
    if (!isDev || !state.isEmulatingRoles) {
      return realRole || '';
    }
    return buildRoleString(state.emulatedRoles);
  }, [state.isEmulatingRoles, state.emulatedRoles, realRole]);

  const toggleSucursalEmulation = useCallback(() => {
    if (!isDev) return;
    setState((prev) => ({ ...prev, isClearingSucursales: !prev.isClearingSucursales }));
  }, []);

  // In production, just return children without context functionality
  const prodValue = useMemo(
    () => ({
      isEmulatingRoles: false,
      emulatedRoles: [],
      realRoles: [],
      setEmulatedRoles: () => {},
      clearEmulation: () => {},
      toggleRole: () => {},
      applyPreset: () => {},
      getEffectiveRole: () => realRole || '',
      isClearingSucursales: false,
      toggleSucursalEmulation: () => {},
    }),
    [realRole]
  );

  if (!isDev) {
    return <DevRoleContext.Provider value={prodValue}>{children}</DevRoleContext.Provider>;
  }

  const devValue = useMemo(
    () => ({
      ...state,
      realRoles,
      setEmulatedRoles,
      clearEmulation,
      toggleRole,
      applyPreset,
      getEffectiveRole,
      toggleSucursalEmulation,
    }),
    [
      state,
      realRoles,
      setEmulatedRoles,
      clearEmulation,
      toggleRole,
      applyPreset,
      getEffectiveRole,
      toggleSucursalEmulation,
    ]
  );

  return <DevRoleContext.Provider value={devValue}>{children}</DevRoleContext.Provider>;
}

DevRoleProvider.propTypes = { children: PropTypes.node.isRequired, realRole: PropTypes.string };

export default DevRoleContext;
